import React, { useEffect, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Clock, CheckCircle, User, EnvelopeSimple, Phone, ArrowRight } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { trackBookingConversion } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = [
  "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
  "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM", "6:00 PM",
];

const SERVICES = [
  "PC Tune-Up",
  "Malware & Virus Removal",
  "Data Backup & Recovery",
  "Remote Support",
  "Network & Wi-Fi Setup",
  "Hardware Repair & Upgrades",
  "Business IT Support",
  "MSP — Essentials Plan",
  "MSP — Professional Plan",
  "MSP — Enterprise Plan",
  "On-Site Diagnostic",
];

const initial = {
  firstName: "", lastName: "", email: "", phone: "",
  service: "", date: null, timeSlot: "", notes: "",
};

export default function BookingDialog({ open, onOpenChange, defaultService = "" }) {
  const [form, setForm] = useState({ ...initial, service: defaultService });
  const [booked, setBooked] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (defaultService) setForm((f) => ({ ...f, service: defaultService }));
  }, [defaultService]);

  useEffect(() => {
    if (!form.date) { setBooked([]); return; }
    const iso = format(form.date, "yyyy-MM-dd");
    axios.get(`${API}/bookings/booked?date=${iso}`).then((r) => setBooked(r.data.booked || [])).catch(() => setBooked([]));
  }, [form.date]);

  const update = (k) => (e) => {
    const val = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: val }));
  };

  const reset = () => {
    setForm({ ...initial, service: defaultService });
    setDone(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.email || !form.phone || !form.service || !form.date || !form.timeSlot) {
      toast.error("Please fill in all required fields and pick a date & time.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        firstName: form.firstName, lastName: form.lastName,
        email: form.email, phone: form.phone, service: form.service,
        date: format(form.date, "yyyy-MM-dd"),
        timeSlot: form.timeSlot, notes: form.notes,
      });
      trackBookingConversion({ event_source: "booking", service: form.service });
      toast.success("Appointment request received.");
      setDone(true);
    } catch (err) {
      toast.error("Couldn't create booking. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setTimeout(reset, 300); }}>
      <DialogContent data-testid="booking-dialog" className="max-w-3xl p-0 overflow-hidden max-h-[92vh] overflow-y-auto">
        <div className="bg-zinc-950 text-white p-6 sm:p-8 relative">
          <div className="absolute inset-0 hero-grid opacity-30" />
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-blue-600/20 border border-blue-500/30 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-widest text-blue-300">
              <CalendarIcon size={11} weight="fill" /> Free Diagnostic
            </div>
            <DialogTitle className="mt-3 font-heading text-2xl sm:text-3xl font-extrabold tracking-tighter text-white">
              Book your appointment
            </DialogTitle>
            <DialogDescription className="mt-1 text-zinc-300 text-sm">
              Pick a date & time — we'll confirm by phone or email within an hour on business days.
            </DialogDescription>
          </div>
        </div>

        {done ? (
          <div className="p-8 sm:p-12 text-center bg-white">
            <CheckCircle size={56} weight="duotone" className="mx-auto text-blue-600" />
            <h3 className="mt-4 font-heading text-2xl font-bold text-zinc-900">You're on the calendar.</h3>
            <p className="mt-2 text-zinc-600 max-w-md mx-auto">
              We've received your request for <span className="font-semibold">{format(form.date, "MMM d, yyyy")} at {form.timeSlot}</span>. We'll reach out shortly to confirm.
            </p>
            <div className="mt-6 flex gap-3 justify-center">
              <Button onClick={() => onOpenChange(false)} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-full px-6" data-testid="booking-close">Close</Button>
              <Button variant="outline" onClick={reset} className="rounded-full px-6" data-testid="booking-book-another">Book another</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} data-testid="booking-form" className="grid lg:grid-cols-2 gap-0">
            {/* Left: Calendar + slots */}
            <div className="p-6 sm:p-8 bg-zinc-50 border-r border-zinc-200">
              <Label className="text-zinc-700 font-semibold mb-2 block">Choose a date</Label>
              <div className="bg-white rounded-xl border border-zinc-200 p-2">
                <Calendar
                  mode="single"
                  selected={form.date}
                  onSelect={update("date")}
                  disabled={(d) => d < today || d.getDay() === 0}
                  data-testid="booking-calendar"
                  className="w-full"
                />
              </div>

              <Label className="text-zinc-700 font-semibold mt-5 mb-2 block flex items-center gap-2">
                <Clock size={15} weight="duotone" /> Available time slots
              </Label>
              <div className="grid grid-cols-3 gap-2">
                {TIME_SLOTS.map((slot) => {
                  const isBooked = booked.includes(slot);
                  const isSelected = form.timeSlot === slot;
                  return (
                    <button
                      type="button"
                      key={slot}
                      disabled={!form.date || isBooked}
                      onClick={() => update("timeSlot")(slot)}
                      data-testid={`booking-slot-${slot.replace(/[\s:]/g, "")}`}
                      className={`text-xs font-medium py-2 rounded-md border transition-all ${
                        isSelected
                          ? "bg-blue-600 text-white border-blue-600"
                          : isBooked
                          ? "bg-zinc-100 text-zinc-400 border-zinc-200 line-through cursor-not-allowed"
                          : !form.date
                          ? "bg-zinc-100 text-zinc-400 border-zinc-200 cursor-not-allowed"
                          : "bg-white text-zinc-800 border-zinc-200 hover:border-blue-400 hover:text-blue-600"
                      }`}
                    >
                      {slot}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right: Contact form */}
            <div className="p-6 sm:p-8 bg-white">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="bf-firstName" className="text-zinc-700 text-xs">First name *</Label>
                  <Input id="bf-firstName" data-testid="booking-firstName" required value={form.firstName} onChange={update("firstName")} placeholder="Jane" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="bf-lastName" className="text-zinc-700 text-xs">Last name</Label>
                  <Input id="bf-lastName" data-testid="booking-lastName" value={form.lastName} onChange={update("lastName")} placeholder="Smith" className="mt-1" />
                </div>
              </div>

              <div className="mt-3">
                <Label htmlFor="bf-email" className="text-zinc-700 text-xs">Email *</Label>
                <Input id="bf-email" type="email" data-testid="booking-email" required value={form.email} onChange={update("email")} placeholder="you@example.com" className="mt-1" />
              </div>

              <div className="mt-3">
                <Label htmlFor="bf-phone" className="text-zinc-700 text-xs">Phone *</Label>
                <Input id="bf-phone" type="tel" data-testid="booking-phone" required value={form.phone} onChange={update("phone")} placeholder="(919) 555-1234" className="mt-1" />
              </div>

              <div className="mt-3">
                <Label htmlFor="bf-service" className="text-zinc-700 text-xs">Service *</Label>
                <Select value={form.service} onValueChange={update("service")}>
                  <SelectTrigger id="bf-service" data-testid="booking-service" className="mt-1">
                    <SelectValue placeholder="Choose a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-3">
                <Label htmlFor="bf-notes" className="text-zinc-700 text-xs">Notes (optional)</Label>
                <Textarea id="bf-notes" data-testid="booking-notes" rows={2} value={form.notes} onChange={update("notes")} placeholder="Anything we should know?" className="mt-1 resize-none" />
              </div>

              <div className="mt-5 flex items-center justify-between rounded-lg bg-zinc-50 border border-zinc-200 p-3 text-sm">
                <div className="text-zinc-500">Your appointment</div>
                <div className="font-semibold text-zinc-900">
                  {form.date ? format(form.date, "MMM d") : "—"} · {form.timeSlot || "—"}
                </div>
              </div>

              <Button type="submit" disabled={submitting} data-testid="booking-submit"
                className="mt-4 w-full h-11 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold blue-glow">
                {submitting ? "Booking…" : (<span className="inline-flex items-center gap-2">Confirm appointment <ArrowRight size={16} weight="bold" /></span>)}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
